import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { Link } from 'react-router-dom';

const EmailVerificationRequired = () => {
  const { user, resendEmailVerification, logout } = useAuth();
  const [resendLoading, setResendLoading] = useState(false);

  const handleResendVerification = async () => {
    try {
      setResendLoading(true);
      
      if (!user?.email) {
        toast.error('No email address found. Please log in again.');
        return;
      }
      
      await resendEmailVerification(user.email);
      toast.success('Verification email sent! Please check your inbox and spam folder.');
      
    } catch (error) {
      console.error('Resend verification failed:', error);
      toast.error('Failed to resend verification email. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="min-h-screen bg-white py-12 px-6 flex items-center justify-center">
      <div className="w-full max-w-md mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center"
        >
          {/* Header */}
          <div className="mb-6">
            <img src="/6.png" alt="SignPact Logo" className="h-12 mx-auto mb-4" />
            <div className="bg-yellow-100 rounded-full p-3 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Email Verification Required
            </h1>
            <p className="text-gray-600">
              Please verify your email address to access your dashboard
            </p>
          </div>

          {/* Email Display */}
          {user?.email && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800">
                <strong>Verification email sent to:</strong><br />
                {user.email}
              </p>
            </div>
          )}

          {/* Instructions */}
          <div className="mb-6 text-left">
            <p className="text-gray-600 text-sm mb-4">
              To access your SignPact dashboard, please:
            </p>
            <ol className="text-gray-600 text-sm space-y-2 list-decimal list-inside">
              <li>Check your email inbox for a verification email from SignPact</li>
              <li>Click the verification link in the email</li>
              <li>Return to this page to access your dashboard</li>
            </ol>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <button
              onClick={handleResendVerification}
              disabled={resendLoading}
              className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {resendLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2 inline-block"></div>
                  Sending...
                </>
              ) : (
                'Resend Verification Email'
              )}
            </button>

            <div className="flex space-x-3">
              <Link
                to="/verify-email"
                className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors text-center text-sm"
              >
                Check Email Status
              </Link>
              
              <button
                onClick={handleLogout}
                className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors text-sm"
              >
                Sign Out
              </button>
            </div>
          </div>

          {/* Help Text */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500">
              Didn't receive the email? Check your spam folder or try resending.
              If you continue to have issues, contact{' '}
              <a href="mailto:support@signpact.com" className="text-blue-600 hover:text-blue-800">
                support@signpact.com
              </a>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default EmailVerificationRequired;